import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  GKClientDataGrid,
  GKTextField,
  Grid2,
  IconButton,
  PercentageIcon,
  RupeeIcon,
  Typography,
} from "components";
import { debounce } from "lodash";
import { useCallback, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { resetColumns } from "store/slices/tableSlice";
import { useAppDispatch } from "store/store";
import { moneyCommaSeparator } from "utils";
import { extraColumnsField } from "utils/helpers";

interface Props {
  userData: any;
  watchAllocationType: string;
}

const PortfolioEditTable = (props: Props) => {
  const { userData, watchAllocationType } = props;

  const dispatch = useAppDispatch();

  const form = useFormContext();

  useEffect(() => {
    dispatch(resetColumns("editPortfolio"));
  }, []);

  const updateCash = useCallback(
    debounce((index, row, value) => {
      if (value === "") {
        form.setValue(`allocation.portfolios.${index}`, {
          ...row,
          percentage: "",
          cash: "",
        });
        return;
      }

      const cashValue =
        form.getValues("allocation.rebalanceUsing") === "committedValue" ||
        form.getValues("allocation.rebalanceUsing") === "commitedValueAbsolute"
          ? row.committedValue
          : !userData?.useCeAmount
            ? row.availableCash
            : row.totalCash;

      const updatedCash =
        ((parseFloat(cashValue) * parseFloat(value)) / 100)?.toFixed(2) || "";

      form.setValue(`allocation.portfolios.${index}`, {
        ...row,
        percentage: +value,
        cash: +updatedCash,
      });
    }, 300),
    [],
  );

  const updatePercentage = useCallback(
    debounce((index, row, value) => {
      if (value === "") {
        form.setValue(`allocation.portfolios.${index}`, {
          ...row,
          cash: "",
          percentage: "",
        });
        return;
      }

      const rebalanceUsing = form.getValues("allocation.rebalanceUsing");

      const cashBase =
        rebalanceUsing === "committedValue" ||
        rebalanceUsing === "commitedValueAbsolute"
          ? row.committedValue
          : !userData?.useCeAmount
            ? row.availableCash
            : row.totalCash;

      const baseAmount =
        watchAllocationType === "exit" ? row.currentAmount : cashBase;

      const calculatedPercentage =
        ((parseFloat(value) / parseFloat(baseAmount)) * 100)?.toFixed(2) || "";

      form.setValue(`allocation.portfolios.${index}`, {
        ...row,
        cash: +value,
        percentage: +calculatedPercentage,
      });
    }, 300),
    [],
  );

  const updateExitWeightage = useCallback(
    debounce((index, row, value) => {
      if (value === "") {
        form.setValue(`allocation.portfolios.${index}`, {
          ...row,
          exitWeightage: "",
        });
        return;
      }

      form.setValue(`allocation.portfolios.${index}`, {
        ...row,
        exitWeightage: +value,
      });
    }, 300),
    [],
  );

  const updateCashForRebalance = useCallback(
    debounce((index, row, value) => {
      if (value === "") {
        form.setValue(`allocation.portfolios.${index}`, {
          ...row,
          cash: "",
          portfolioValue: +row.currentAmount || 0,
        });
        return;
      }

      form.setValue(`allocation.portfolios.${index}`, {
        ...row,
        cash: +value,
        portfolioValue: +row?.currentAmount + +value,
      });
    }, 300),
    [],
  );

  const columns: (GridColDef & { visibleType?: string[] })[] = [
    {
      ...extraColumnsField(
        `Portfolio (${form.getValues(`allocation.portfolios`)?.length || 0})`,
        "clientName",
        250,
        1,
      ),
      visibleType: ["allocation", "exit", "rebalance"],
      sortable: false,
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography
              fontWeight={700}
              maxWidth={"100%"}
              textOverflow={"ellipsis"}
              overflow={"hidden"}
            >
              {params?.row?.clientName}
            </Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioCode}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Current Holdings (₹)", "currentAmount", 120, 1),
      sortable: false,
      type: "number",
      valueGetter: (val) => val?.value || 0,
      visibleType: ["exit", "rebalance"],
      valueFormatter: (val) => moneyCommaSeparator(val?.value),
    },
    {
      ...extraColumnsField("Ledger balance (₹)", "availableCash", 150, 1),
      visibleType: ["allocation"],
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("committed value (₹)", "committedValue", 160, 1),
      visibleType: ["allocation"],
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Allocation", "percentage", 130, 1),
      visibleType: ["allocation"],
      type: "number",
      renderCell: (params) => {
        const { index } = params?.row;

        return (
          <Controller
            control={form.control}
            name={`allocation.portfolios.${index}.percentage`}
            render={({ field }) => (
              <GKTextField
                {...field}
                placeholder="Allocation Percentage"
                size="small"
                type="number"
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val === "" ? "" : Number(val));
                  updateCash(index, params.row, val);
                }}
                slotProps={{ input: { endAdornment: <PercentageIcon /> } }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Allocation Amount (₹)", "cash", 180, 1),
      visibleType: ["allocation"],
      type: "number",
      sortable: false,
      renderCell: (params) => {
        const { index } = params?.row;

        return (
          <Controller
            control={form.control}
            name={`allocation.portfolios.${index}.cash`}
            render={({ field }) => (
              <GKTextField
                {...field}
                size="small"
                placeholder="Amount"
                type="number"
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val === "" ? "" : Number(val));
                  updatePercentage(index, params.row, val);
                }}
                slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Exit Weightage", "exitWeightage", 130, 1),
      sortable: false,
      visibleType: ["exit"],
      renderCell: (params) => {
        const { index } = params?.row;
        return (
          <Controller
            control={form.control}
            name={`portfolios.${index}.exitWeightage`}
            render={({ field }) => (
              <GKTextField
                {...field}
                placeholder="Amount"
                size="small"
                type="number"
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val === "" ? "" : Number(val));
                  updateExitWeightage(index, params.row, val);
                }}
                slotProps={{ input: { endAdornment: <PercentageIcon /> } }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Cash for Rebalance", "Cash for Rebalance", 120, 1),
      sortable: false,
      visibleType: ["rebalance"],
      renderCell: (params) => {
        const { index, totalCash } = params.row;
        return (
          <Controller
            control={form.control}
            name={`allocation.portfolios.${index}.cash`}
            defaultValue={totalCash}
            render={({ field }) => (
              <GKTextField
                {...field}
                size="small"
                placeholder="Cash for Rebalance"
                type="number"
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val === "" ? "" : Number(val));
                  updateCashForRebalance(index, params.row, val);
                }}
                slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Total amount (₹)", "portfolioValue", 120, 1),
      sortable: false,
      type: "number",
      visibleType: ["rebalance"],
      renderCell: (params) => {
        const { index } = params.row;
        return (
          <Controller
            control={form.control}
            name={`allocation.portfolios.${index}.portfolioValue`}
            render={({ field }) => (
              <Typography fontSize={14}>
                {moneyCommaSeparator(field.value ?? 0)}
              </Typography>
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Actions", "action", 100, 1),
      visibleType: ["allocation", "exit", "rebalance"],
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            form.setValue(
              "allocation.portfolios",
              form
                .getValues("allocation.portfolios")
                .filter(
                  (dataItem: { portfolioId: number }) =>
                    dataItem.portfolioId !== params.row.portfolioId,
                ),
            );
          }}
        >
          <DeleteIcon color=" #C35245" />
        </IconButton>
      ),
    },
  ];

  return (
    <Grid2 container>
      <Grid2 size={12}>
        <Controller
          name="allocation.portfolios"
          control={form.control}
          render={({ field }) => {
            return (
              <GKClientDataGrid
                name="editPortfolio"
                disableRowSelectionOnClick
                columns={columns.filter((column) => {
                  return column.visibleType?.includes(watchAllocationType);
                })}
                rows={
                  field?.value?.map((da: any, i: any) => {
                    return {
                      ...da,
                      index: i,
                    };
                  }) || []
                }
                getRowId={(op) => op?.portfolioId}
                maxHeight={`calc(100vh - 271px)`}
                showHeaderGrid={false}
              />
            );
          }}
        />
      </Grid2>
    </Grid2>
  );
};

export default PortfolioEditTable;
